import React, { useRef } from "react";
import { getMinEventDateValue, getTodayDateValue, isDateBeforeMinEventDate } from "../dateUtils";

export default function AgencyStep1Form({
  form,
  handleChange,
  handleSubmit,
  loading,
  price,
  proceedToStep2,
  handleDownloadPDF,
  pdfLibReady,
  pdfDownloading,
  formatCurrency,
  setTimeSlot,
  locationRef,
  t,
}) {
  const eventDateRef = useRef(null);
  const indemnity = Number(form.amount || 0) * 0.7;
  const minEventDateValue = getMinEventDateValue();
  const todayDateValue = getTodayDateValue();

  const openDatePickerFromToday = () => {
    const input = eventDateRef.current;
    if (!input || form.start_date) return;

    input.value = todayDateValue;
    window.setTimeout(() => {
      if (!form.start_date && input.value === todayDateValue) {
        input.value = "";
      }
    }, 250);
  };

  const handleDateChange = (e) => {
    if (isDateBeforeMinEventDate(e.target.value)) {
      e.target.value = "";
      handleChange({ target: { name: "start_date", value: "" } });
      return;
    }

    handleChange(e);
  };

  return (
    <form onSubmit={handleSubmit} className="agency-form agency-form-step1">
      <div className="agency-panel agency-panel-top">
        <h2 className="agency-title">
          <span className="agency-title-bolt">⚡</span> {t.heroBefore} <span>{t.heroHighlight}</span>{" "}
          {t.heroAfter}
        </h2>

        <div className="agency-grid-two">
          <div>
            <label className="agency-label">🗓️ {t.eventDate}</label>
            <input
              type="date"
              name="start_date"
              ref={eventDateRef}
              value={form.start_date}
              onChange={handleDateChange}
              onFocus={openDatePickerFromToday}
              onPointerDown={openDatePickerFromToday}
              min={minEventDateValue}
              required
            />
          </div>

          <div>
            <label className="agency-label">🏰 {t.location}</label>
            <input
              type="text"
              name="location"
              ref={locationRef}
              value={form.location}
              onChange={handleChange}
              placeholder={t.locationPlaceholder}
              autoComplete="off"
              required
            />
          </div>
        </div>

        <div className="agency-row">
          <label className="agency-label agency-label-with-tooltip">
            <span>💰 {t.budget}</span>
            <span className="agency-tooltip" tabIndex="0" aria-label={t.budget}>
              ?
              <span className="agency-tooltip-content" role="tooltip">
                {t.budgetTooltip}
                <br />
                {t.budgetTooltipSecond}
              </span>
            </span>
          </label>
          <input
            type="number"
            name="amount"
            value={form.amount}
            onChange={handleChange}
            placeholder={t.amountPlaceholder}
            min="500"
            step="100"
            required
          />
          <div className="agency-indemnity">
            {t.indemnity} (70%): <strong>{formatCurrency(indemnity)}</strong>
          </div>
        </div>

        <div className="agency-row agency-rain-row">
          <div className="agency-rain-label agency-label-with-tooltip">
            <span>
              🌧️ {t.rainThreshold}: <strong>5 mm</strong>
            </span>
            <span className="agency-tooltip" tabIndex="0" aria-label={t.rainThreshold}>
              ?
              <span className="agency-tooltip-content" role="tooltip">
                {t.rainTooltip}
              </span>
            </span>
          </div>
          <div className="agency-rain-subtitle">
            <span className="agency-subtitle-with-tooltip">
              <span>🕓 {t.weddingTime}</span>
              <span className="agency-tooltip" tabIndex="0" aria-label={t.weddingTime}>
                ?
                <span className="agency-tooltip-content" role="tooltip">
                  {t.weddingTimeTooltip}
                </span>
              </span>
            </span>
          </div>

          <div className="agency-time-slots">
            <button
              type="button"
              className={`agency-time-slot ${form.time_slot === "morning" ? "active" : ""}`}
              onClick={() => setTimeSlot("morning")}
            >
              <span>{t.morning}</span>
              <small>11:00 - 17:00</small>
            </button>

            <button
              type="button"
              className={`agency-time-slot ${form.time_slot === "afternoon" ? "active" : ""}`}
              onClick={() => setTimeSlot("afternoon")}
            >
              <span>{t.afternoon}</span>
              <small>18:00 - 24:00</small>
            </button>
          </div>
        </div>

        <div className="agency-row">
          <label className="agency-label">✉️ Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            required
          />
        </div>
      </div>

      <div className="agency-panel agency-panel-bottom">
        <label className="agency-checkline">
          <input
            type="checkbox"
            name="step1_privacy_seen"
            checked={form.step1_privacy_seen}
            onChange={handleChange}
            required
          />
          <span>📄 {t.privacySeen}</span>
        </label>

        <label className="agency-checkline">
          <input
            type="checkbox"
            name="step1_marketing"
            checked={form.step1_marketing}
            onChange={handleChange}
          />
          <span>📬 {t.marketing}</span>
        </label>

        <label className="agency-checkline">
          <input
            type="checkbox"
            name="step1_personalization"
            checked={form.step1_personalization}
            onChange={handleChange}
          />
          <span>✨ {t.personalization}</span>
        </label>

        <button type="submit" className="agency-btn agency-btn-primary" disabled={loading}>
          {loading ? t.calculateLoading : t.calculate}
        </button>

        {price != null && (
          <>
            <div className="agency-price-result">💰 {t.estimatedPremium}: <strong>{formatCurrency(price)}</strong></div>
            <button type="button" className="agency-btn agency-btn-secondary" onClick={proceedToStep2}>
              {t.proceed}
            </button>
            <button
              type="button"
              className="agency-btn agency-btn-ghost"
              onClick={handleDownloadPDF}
              disabled={!pdfLibReady || pdfDownloading}
            >
              {pdfDownloading ? t.pdfLoading : `📄 ${t.downloadPdf}`}
            </button>
          </>
        )}
      </div>
    </form>
  );
}
